import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import * as PropTypes from 'prop-types';
import CSSModules from 'react-css-modules';
import moment from 'moment';

import {
  HEALTHBOARD,
  HEALTHBOARD_APPOINTMENTS_HISTORY,
} from '../../../../constants/routes';
import { VIRTUAL_VISIT_TYPE_CODE } from '../../../../constants';
import { useLazyLoad } from '../../../../hocs/useLazyLoad';
import { CommonTabs } from '../../../common/CommonTabs';
import { IDS } from '../../../../constants/ids';
import { AppointmentFilter } from '../AppointmentFilter';
import ScheduleButton from '../ScheduleButton';
import AppointmentList from '../AppointmentList';
import ListWrapper from '../ListWrapper';
import styles from './styles.styl';
import { usePageView } from '../../../../hocs/usePageView';
import MetaHelmet from '../../../AppHelmet/MetaHelmet';

const TAB_KEYS = {
  UPCOMING: 'upcoming',
  HISTORY: 'history',
};

function AppointmentTabs({
  fetchUpcomingList,
  loadMoreUpcomingList,
  fetchHistoryList,
  loadMoreHistoryList,
  upcomingList,
  upcomingCount,
  upcomingLoading,
  historyLoading,
  historyList,
  historyCount,
  renderTabHeader,
  providerContext,
  providerView,
  intl: { formatMessage },
  providerContextHistory,
  setAppointmentFiltersOption,
  isFilterByTypeVisit,
  isFilterToday,
  paginationSending,
  id,
}) {
  const [activeTab, setActiveTab] = useState(TAB_KEYS.UPCOMING);
  usePageView('Home | Appointment List');

  const todayDate = moment().format('YYYY-MM-DD');

  const tabs = useTabs(upcomingCount, historyCount);
  const rootRef = useRef();
  const loadMore = useCallback(() => {
    if (paginationSending) return;
    if (activeTab === TAB_KEYS.UPCOMING)
      loadMoreUpcomingList({
        id,
        ...(isFilterToday && { date: todayDate }),
        ...(isFilterByTypeVisit && {
          visit_type_code: VIRTUAL_VISIT_TYPE_CODE,
        }),
      });
    if (activeTab === TAB_KEYS.HISTORY) loadMoreHistoryList({ id });
  }, [activeTab, id, isFilterToday, isFilterByTypeVisit, paginationSending]);
  const isActiveTabUpcoming = activeTab === TAB_KEYS.UPCOMING;

  const mainTitle = formatMessage({
    id: `healthboard.appointments.${
      isActiveTabUpcoming ? TAB_KEYS.UPCOMING : TAB_KEYS.HISTORY
    }`,
  });

  const actionText = isActiveTabUpcoming
    ? formatMessage({ id: 'healthboard.action.seeCompleted' })
    : '';

  useLazyLoad({ node: rootRef, loadMore, path: [] });
  useEffect(() => {
    if (!providerContextHistory)
      fetchUpcomingList({
        limit: 10,
        offset: 0,
        ...(isFilterToday && { date: todayDate }),
        ...(isFilterByTypeVisit && {
          visit_type_code: VIRTUAL_VISIT_TYPE_CODE,
        }),
        id,
      });
    if (providerContextHistory || !providerView)
      fetchHistoryList({ limit: 10, offset: 0, id });
  }, [
    id,
    providerContextHistory,
    isFilterToday,
    isFilterByTypeVisit,
    providerView,
  ]);

  useEffect(() => {
    if (providerContextHistory) {
      setActiveTab(TAB_KEYS.HISTORY);
    }
  }, [providerContextHistory]);

  const getContentData = tab => {
    switch (tab) {
      case TAB_KEYS.UPCOMING:
        return {
          list: upcomingList,
          loading: upcomingLoading,
          paginationSending,
          providerContext,
          providerView,
          id,
          isUpcoming: true,
        };
      case TAB_KEYS.HISTORY:
        return {
          list: historyList,
          loading: historyLoading,
          paginationSending,
          providerContext,
          providerView,
          id,
          isUpcoming: false,
        };
      default:
        return { list: [], loading: false };
    }
  };

  useEffect(() => {
    if (setAppointmentFiltersOption)
      setAppointmentFiltersOption({
        isFilterToday: true,
        isFilterByTypeVisit: false,
      });
  }, []);

  return (
    <div styleName="root" ref={rootRef}>
      <MetaHelmet title="pages.title.homeAppointments" />

      <div styleName="content">
        {providerView || providerContextHistory ? (
          <ListWrapper
            title={mainTitle}
            counter={isActiveTabUpcoming ? upcomingCount : historyCount}
            actionText={actionText}
            renderHeaderButton={() => <ScheduleButton userId={id} />}
            redirectTo={`${HEALTHBOARD}/${id}/${HEALTHBOARD_APPOINTMENTS_HISTORY}`}
          >
            {isActiveTabUpcoming && (
              <AppointmentFilter
                formatMessage={formatMessage}
                isFilterByTypeVisit={isFilterByTypeVisit}
                isFilterToday={isFilterToday}
                setAppointmentFiltersOption={setAppointmentFiltersOption}
              />
            )}
            <AppointmentList
              {...getContentData(
                isActiveTabUpcoming ? TAB_KEYS.UPCOMING : TAB_KEYS.HISTORY,
              )}
            />
          </ListWrapper>
        ) : (
          <CommonTabs
            onTabSelect={setActiveTab}
            activeTab={activeTab}
            tabList={tabs}
            renderHeader={renderTabHeader}
            renderContent={tab => <AppointmentList {...getContentData(tab)} />}
          />
        )}
      </div>
    </div>
  );
}

AppointmentTabs.propTypes = {
  fetchUpcomingList: PropTypes.func,
  loadMoreUpcomingList: PropTypes.func,
  fetchHistoryList: PropTypes.func,
  loadMoreHistoryList: PropTypes.func,
  upcomingList: PropTypes.array,
  upcomingCount: PropTypes.number,
  upcomingLoading: PropTypes.bool,
  historyLoading: PropTypes.bool,
  historyList: PropTypes.array,
  historyCount: PropTypes.number,
  renderTabHeader: PropTypes.func,
  providerContext: PropTypes.bool,
  providerView: PropTypes.bool,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  providerContextHistory: PropTypes.bool,
  intl: PropTypes.object,
  setAppointmentFiltersOption: PropTypes.func,
  isFilterByTypeVisit: PropTypes.bool,
  isFilterToday: PropTypes.bool,
  paginationSending: PropTypes.bool,
};

AppointmentTabs.defaultProps = {
  providerContext: false,
  providerView: false,
};

export default CSSModules(AppointmentTabs, styles, { allowMultiple: true });

function useTabs(upcomingCounter, completedCounter) {
  return useMemo(
    () => [
      {
        label: 'appointment.list.tabs.upcoming',
        tab: TAB_KEYS.UPCOMING,
        id: IDS.APPOINTMENT_LIST.TAB_UPCOMING,
        counter: upcomingCounter,
      },
      {
        label: 'appointment.list.tabs.history',
        tab: TAB_KEYS.HISTORY,
        id: IDS.APPOINTMENT_LIST.TAB_HISTORY,
        counter: completedCounter,
      },
    ],
    [upcomingCounter, completedCounter],
  );
}



// WEBPACK FOOTER //
// ./src/components/Healthboard/Components/AppointmentTabs/index.jsx